import { CompanyService } from '@beeboard/common';
import { defineStore } from 'pinia';
import { useCompanyActivityRulesStore } from './companyActivityRulesStore';
import { useCompanyDocumentStore } from './companyDocumentStore';
import { useCompanyLinkStore } from './companyLinkStore';
import { useCompanyTagStore } from './companyTagStore';
import { useFunctionStore } from './functionStore';
import type {
  AddCompanyActivityApproverRequest,
  AddCompanyActivityDocumentRequest,
  AddCompanyActivityLinkRequest,
  AddCompanyActivityWatcherRequest,
  CompanyActivity,
  CompanyActivityDuplicateRequest,
  CompanyActivitySummary,
  CompanyCompletionCriterion,
  CompanySearchResultItem,
  CompanyTag,
  CreateCompanyActivityRequest,
  CreateCompanyCompletionCriterionRequest,
  Function,
  UpdateCompanyActivityRequest,
  UpdateCompanyCompletionCriterionRequest,
  CompanyActivityLabel,
  CompanyActivityDetails,
} from '@beeboard/common';

export type GroupedCompanyActivity = CompanyActivityDetails & {
  children: GroupedCompanyActivity[];
};

export type ActivityStoreState = {
  companyActivitiesSummary: CompanyActivitySummary[];
  companyActivitiesDetails: CompanyActivityDetails[];
  companyActivities: CompanyActivity[];
  companyActivityLabels: CompanyActivityLabel[];
};

export const useCompanyActivityStore = defineStore('companyActivityStore', {
  state: (): ActivityStoreState => ({
    companyActivitiesSummary: [] as CompanyActivitySummary[],
    companyActivitiesDetails: [] as CompanyActivityDetails[],
    companyActivities: [] as CompanyActivity[],
    companyActivityLabels: [] as CompanyActivityLabel[],
  }),

  getters: {
    companyActivitySummary(state) {
      return (companyActivityId: string): CompanyActivitySummary => {
        const activity = state.companyActivitiesSummary.find(
          (activity) => activity.id === companyActivityId,
        );
        if (!activity) {
          throw new Error(
            `Missing companyActivity ${companyActivityId} in companyActivity store`,
          );
        }

        return activity;
      };
    },

    companyActivity(state) {
      return (companyActivityId: string): CompanyActivity => {
        const activity = state.companyActivities.find(
          (activity) => activity.id === companyActivityId,
        );
        if (!activity) {
          throw new Error(
            `Missing companyActivity ${companyActivityId} in companyActivity store`,
          );
        }

        return activity;
      };
    },

    companyActivityDetails(state) {
      return (companyActivityId: string): CompanyActivityDetails => {
        const activity = state.companyActivitiesDetails.find(
          (activity) => activity.id === companyActivityId,
        );
        if (!activity) {
          throw new Error(
            `Missing companyActivity ${companyActivityId} in companyActivity store`,
          );
        }

        return activity;
      };
    },

    groupedCompanyActivities(): GroupedCompanyActivity[] {
      return this._buildGrouped(this.companyActivitiesSummary);
    },

    groupedCompanyActivitiesDetails(): GroupedCompanyActivity[] {
      return this._buildGrouped(this.companyActivitiesDetails);
    },

    _buildGrouped() {
      return (
        activities: CompanyActivityDetails[] | CompanyActivitySummary[],
      ): GroupedCompanyActivity[] => {
        const activityMap = new Map();
        const groupedActivities: GroupedCompanyActivity[] = [];

        // Initialize the map with all activities
        for (const activity of activities) {
          activityMap.set(activity.id, { ...activity, children: [] });
        }

        // Assign children to their parents
        for (const activity of activities) {
          if (activity.parent) {
            const parent = activityMap.get(activity.parent);
            if (parent) {
              parent.children.push(activityMap.get(activity.id));
            }
          }
        }

        // Collect top-level activities (those without parents)
        for (const activity of activities) {
          if (!activity.parent) {
            groupedActivities.push(activityMap.get(activity.id));
          }
        }

        return groupedActivities;
      };
    },

    hasCompanyActivity(state) {
      return (companyActivityId: string): boolean => {
        return state.companyActivities.some(
          (activity) => activity.id === companyActivityId,
        );
      };
    },

    companyCriterion() {
      return (activityId: string, criterionId: string) => {
        const companyActivity = this.companyActivity(activityId);
        const companyCriterion = companyActivity.completion_criteria.find(
          ({ id }) => criterionId === id,
        );
        if (!companyCriterion) {
          throw new Error('Missing completion criterion in company activity');
        }

        return companyCriterion;
      };
    },

    approvers() {
      return (activityId: string): Function[] => {
        const approverIds = this.companyActivity(activityId).approvers;

        return approverIds.map((id) => {
          const approver = useFunctionStore().function(id);
          if (!approver) {
            throw new Error('Missing function in company');
          }

          return approver;
        });
      };
    },

    watchers() {
      return (activityId: string): Function[] => {
        const watcherIds = this.companyActivity(activityId).watchers;

        return watcherIds.map((id) => {
          const watcher = useFunctionStore().function(id);
          if (!watcher) {
            throw new Error('Missing function in company');
          }

          return watcher;
        });
      };
    },

    availableFunctions() {
      return (activityId: string): Function[] => {
        const activity = this.companyActivity(activityId);

        return useFunctionStore().functions.filter(
          ({ id }) =>
            id !== activity.responsible &&
            !activity.watchers.includes(id) &&
            !activity.approvers.includes(id),
        );
      };
    },

    availableFunctionsForResponsible() {
      return (activityId: string): Function[] => {
        const activity = this.companyActivity(activityId);

        return useFunctionStore().functions.filter(
          ({ id }) =>
            !activity.watchers.includes(id) && !activity.approvers.includes(id),
        );
      };
    },

    currentTags() {
      return (activityId: string): CompanyTag[] => {
        const tags = this.companyActivity(activityId).tags;

        return tags
          .map((id) => useCompanyTagStore().companyTag(id))
          .sort((a, b) => a.title.localeCompare(b.title));
      };
    },
  },

  actions: {
    writeSummary(companyActivitySummary: CompanyActivitySummary) {
      const companyActivitiesSummary = this.companyActivitiesSummary;
      const oldActivity = companyActivitiesSummary.find(
        (a) => a.id === companyActivitySummary.id,
      );
      if (oldActivity) {
        Object.assign(oldActivity, companyActivitySummary);
      } else {
        companyActivitiesSummary.push(companyActivitySummary);
      }
    },

    writeDetails(companyActivityDetails: CompanyActivityDetails) {
      const companyActivitiesDetails = this.companyActivitiesDetails;
      const oldActivity = companyActivitiesDetails.find(
        (a) => a.id === companyActivityDetails.id,
      );
      if (oldActivity) {
        Object.assign(oldActivity, companyActivityDetails);
      } else {
        companyActivitiesDetails.push(companyActivityDetails);
      }
    },

    write(companyActivity: CompanyActivity) {
      const companyActivities = this.companyActivities;
      const oldActivity = companyActivities.find(
        (act) => act.id === companyActivity.id,
      );
      if (oldActivity) {
        Object.assign(oldActivity, companyActivity);
      } else {
        companyActivities.push(companyActivity);
      }

      return this.companyActivity(companyActivity.id);
    },

    writeCriterion(activityId: string, payload: CompanyCompletionCriterion) {
      const companyActivity = this.companyActivity(activityId);
      const criterion = companyActivity.completion_criteria.find(
        ({ id }) => id === payload.id,
      );

      if (criterion) {
        Object.assign(criterion, payload);
      } else {
        companyActivity.completion_criteria.push(payload);
      }

      return this.companyCriterion(activityId, payload.id);
    },

    async loadCompanyActivities() {
      const companyActivitiesSummary =
        await CompanyService.getCompanyActivities();
      this.$patch({ companyActivitiesSummary });
    },

    async loadCompanyActivitiesDetails() {
      const companyActivitiesDetails =
        await CompanyService.getCompanyActivitiesDetails();
      this.$patch({ companyActivitiesDetails });
    },

    async loadCompanyActivity(companyActivityId: string) {
      const companyActivity =
        await CompanyService.getCompanyActivity(companyActivityId);
      this.write(companyActivity);

      return this.companyActivity(companyActivityId);
    },

    async create(payload: CreateCompanyActivityRequest) {
      const errors =
        useCompanyActivityRulesStore().validateCreateCompanyActivityRequest(
          payload,
        );
      if (errors) {
        throw errors;
      }
      const companyActivity = await CompanyService.addCompanyActivity(payload);
      this.writeDetails(companyActivity);

      return companyActivity;
    },

    async update(activityId: string, payload: UpdateCompanyActivityRequest) {
      const errors =
        useCompanyActivityRulesStore().validateUpdateCompanyActivityRequest(
          activityId,
          payload,
        );
      if (errors) {
        throw errors;
      }
      const companyActivity = await CompanyService.updateCompanyActivity(
        activityId,
        payload,
      );
      this.write(companyActivity);
    },

    async delete(companyActivityId: string) {
      await CompanyService.deleteCompanyActivity(companyActivityId);
      const companyActivities = this.$state.companyActivities;
      const activityIndex = companyActivities.findIndex(
        (a) => a.id === companyActivityId,
      );
      if (activityIndex > -1) {
        companyActivities.splice(activityIndex, 1);
      }
      const companyActivitiesSummary = this.$state.companyActivitiesSummary;
      const activitySummaryIndex = companyActivitiesSummary.findIndex(
        (a) => a.id === companyActivityId,
      );
      if (activitySummaryIndex > -1) {
        companyActivitiesSummary.splice(activitySummaryIndex, 1);
      }
      const companyActivitiesDetails = this.$state.companyActivitiesDetails;
      const activityDetailsIndex = companyActivitiesDetails.findIndex(
        (a) => a.id === companyActivityId,
      );
      if (activityDetailsIndex > -1) {
        companyActivitiesDetails.splice(activityDetailsIndex, 1);
      }
    },

    duplicate(companyActivityId: string) {
      const payload: CompanyActivityDuplicateRequest = {
        duplicate_id: companyActivityId,
      };

      return CompanyService.duplicateCompanyActivity(payload)
        .then((createdCompanyActivity) => {
          this.writeDetails(createdCompanyActivity);
        })
        .catch((error) => {
          throw error;
        });
    },

    async createCompletionCriterion(
      activityId: string,
      payload: CreateCompanyCompletionCriterionRequest,
      index?: number,
    ) {
      const completionCriterion =
        await CompanyService.createCompanyCompletionCriterion(
          activityId,
          payload,
          index,
        );

      return this.writeCriterion(activityId, completionCriterion);
    },

    async deleteCompletionCriterion(
      activityId: string,
      completionCriterionId: string,
    ) {
      await CompanyService.deleteCompanyCompletionCriterion(
        activityId,
        completionCriterionId,
      );
      const companyActivity = await this.loadCompanyActivity(activityId);
      if (!companyActivity) {
        throw new Error('Missing Company activity');
      }
      if (
        companyActivity.completion_criteria?.find(
          (p) => p.id === completionCriterionId,
        )
      ) {
        return;
      }
      const critIndex = companyActivity.completion_criteria.findIndex(
        ({ id }) => completionCriterionId === id,
      );
      if (critIndex > -1) {
        companyActivity.completion_criteria.splice(critIndex, 1);
      }
    },

    async updateCriterion(
      activityId: string,
      completionCriterionId: string,
      payload: UpdateCompanyCompletionCriterionRequest,
    ) {
      const completionCriterion =
        await CompanyService.updateCompanyCompletionCriterion(
          activityId,
          completionCriterionId,
          payload,
        );

      return this.writeCriterion(activityId, completionCriterion);
    },

    async updateCompletionCriteriaOrder(activityId: string, order: string[]) {
      const payload = {
        completion_criteria_order: order,
      };
      const errors =
        useCompanyActivityRulesStore().validateUpdateCompanyActivityRequest(
          activityId,
          payload,
        );
      if (errors) {
        throw errors;
      }

      return CompanyService.updateCompanyActivity(activityId, payload).then(
        (activity) => {
          this.write(activity);
        },
      );
    },

    async addDocumentToCompanyActivity(
      activityId: string,
      payload: AddCompanyActivityDocumentRequest,
    ) {
      const errors =
        useCompanyActivityRulesStore().validateAddDocumentToCompanyActivity(
          activityId,
          payload.id,
        );
      if (errors) {
        throw errors;
      }
      await CompanyService.companyActivityAddDocument(activityId, payload);
      await this.loadCompanyActivity(activityId);
      await useCompanyDocumentStore().loadDocument(payload.id);
    },

    async removeDocumentFromCompanyActivity(
      activityId: string,
      documentId: string,
    ) {
      const errors =
        useCompanyActivityRulesStore().validateRemoveDocumentFromCompanyActivity(
          activityId,
          documentId,
        );
      if (errors) {
        throw errors;
      }
      await CompanyService.companyActivityRemoveDocument(
        activityId,
        documentId,
      );
      await this.loadCompanyActivity(activityId);
      await useCompanyDocumentStore().loadDocument(documentId);
    },

    async addLinkToCompanyActivity(
      activityId: string,
      payload: AddCompanyActivityLinkRequest,
    ) {
      const errors =
        useCompanyActivityRulesStore().validateAddLinkToCompanyActivity(
          activityId,
          payload.id,
        );
      if (errors) {
        throw errors;
      }
      await CompanyService.companyActivityAddLink(activityId, payload);
      await this.loadCompanyActivity(activityId);
      await useCompanyLinkStore().loadCompanyLink(payload.id);
    },

    async removeLinkFromCompanyActivity(activityId: string, linkId: string) {
      const errors =
        useCompanyActivityRulesStore().validateRemoveLinkFromCompanyActivity(
          activityId,
          linkId,
        );
      if (errors) {
        throw errors;
      }

      await CompanyService.companyActivityRemoveLink(activityId, linkId);
      await this.loadCompanyActivity(activityId);
      await useCompanyLinkStore().loadCompanyLink(linkId);
    },

    async addApproverToCompanyActivity(
      activityId: string,
      payload: AddCompanyActivityApproverRequest,
    ) {
      const errors =
        useCompanyActivityRulesStore().validateAddApproverToCompanyActivity(
          activityId,
          payload.id,
        );
      if (errors) {
        throw errors;
      }
      await CompanyService.companyActivityAddApprover(activityId, payload);
      const activity = this.companyActivity(activityId);
      activity.approvers.push(payload.id);
      if (activity.parent) {
        activity.override_approver = true;
      }
    },

    async removeApproverFromCompanyActivity(
      activityId: string,
      approverId: string,
    ) {
      const errors =
        useCompanyActivityRulesStore().validateRemoveApproverFromCompanyActivity(
          activityId,
          approverId,
        );
      if (errors) {
        throw errors;
      }
      await CompanyService.companyActivityRemoveApprover(
        activityId,
        approverId,
      );
      const activity = this.companyActivity(activityId);
      if (!activity) {
        throw new Error('Missing company acivity in store');
      }
      const approverIndex = activity.approvers.findIndex(
        (id) => id === approverId,
      );
      activity.approvers.splice(approverIndex, 1);
      if (activity.parent) {
        activity.override_approver = true;
      }
    },

    async addWatcherToCompanyActivity(
      activityId: string,
      payload: AddCompanyActivityWatcherRequest,
    ) {
      const errors =
        useCompanyActivityRulesStore().validateAddWatcherToCompanyActivity(
          activityId,
          payload.id,
        );
      if (errors) {
        throw errors;
      }
      await CompanyService.companyActivityAddWatcher(activityId, payload);
      const activity = this.companyActivity(activityId);
      activity.watchers.push(payload.id);
      if (activity.parent) {
        activity.override_watcher = true;
      }
    },

    async removeWatcherFromCompanyActivity(
      activityId: string,
      watcherId: string,
    ) {
      const errors =
        useCompanyActivityRulesStore().validateRemoveWatcherFromCompanyActivity(
          activityId,
          watcherId,
        );
      if (errors) {
        throw errors;
      }
      await CompanyService.companyActivityRemoveWatcher(activityId, watcherId);
      const activity = this.companyActivity(activityId);
      if (!activity) {
        throw new Error('Missing company acivity in store');
      }
      const watcherIndex = activity.watchers.findIndex(
        (id) => id === watcherId,
      );
      activity.watchers.splice(watcherIndex, 1);
      if (activity.parent) {
        activity.override_watcher = true;
      }
    },

    async addTagToCompanyActivity(
      activityId: string,
      payload: AddCompanyActivityWatcherRequest,
    ) {
      const errors =
        useCompanyActivityRulesStore().validateAddTagToCompanyActivity(
          activityId,
          payload,
        );
      if (errors) {
        throw errors;
      }
      await CompanyService.companyActivityAddTag(activityId, payload);
      await this.loadCompanyActivity(activityId);
      await useCompanyTagStore().loadCompanyTag(payload.id);
    },

    async removeTagFromCompanyActivity(activityId: string, tagId: string) {
      const errors =
        useCompanyActivityRulesStore().validateRemoveTagFromCompanyActivity(
          activityId,
          tagId,
        );
      if (errors) {
        throw errors;
      }
      await CompanyService.companyActivityRemoveTag(activityId, tagId);
      await this.loadCompanyActivity(activityId);
      await useCompanyTagStore().loadCompanyTag(tagId);
    },

    async loadCompanyActivityLabels() {
      const loadedLabels = await CompanyService.getCompanyActivityLabels();
      const sortedLabels = loadedLabels.sort((a, b) =>
        a.title.localeCompare(b.title),
      );
      this.$patch({ companyActivityLabels: sortedLabels });
    },

    async createLabelForCompanyActivities(title: string) {
      const labelTitles = this.companyActivityLabels.map(
        (label) => label.title,
      );
      const errors =
        useCompanyActivityRulesStore().validateIfUniqueCompanyActivityLabel(
          title,
          labelTitles,
        );

      if (errors) {
        throw errors;
      }

      const newLabel = await CompanyService.createCompanyActivityLabel({
        title,
      });
      this.companyActivityLabels.push(newLabel);
      this.companyActivityLabels.sort((a, b) => a.title.localeCompare(b.title));

      return newLabel;
    },

    async updateLabelForCompanyActivities(labelId: string, title: string) {
      let labelIndex = -1;
      const labelTitles = this.companyActivityLabels.map((label, index) => {
        if (label.id === labelId) {
          labelIndex = index;
        }

        return label.title;
      });

      const errors =
        useCompanyActivityRulesStore().validateIfUniqueCompanyActivityLabel(
          title,
          labelTitles,
        );

      if (errors) {
        throw errors;
      }

      const updatedLabel = await CompanyService.updateCompanyActivityLabel(
        labelId,
        { title },
      );

      // update persisted activity
      if (labelIndex !== -1) {
        this.companyActivityLabels.splice(labelIndex, 1, updatedLabel);
      }
    },

    async deleteLabelForCompanyActivities(labelId: string) {
      await CompanyService.deleteCompanyActivityLabel(labelId);

      this.companyActivityLabels = this.companyActivityLabels.filter(
        (label) => label.id !== labelId,
      );
    },

    async performSemanticSearch(
      query: string,
    ): Promise<CompanySearchResultItem[]> {
      return CompanyService.searchCompanyActivities(query);
    },
  },
});
